import { FiCheck, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { CurrencyValue } from '../ui/CurrencyValue';
import { StatusBadge } from '../ui/StatusBadge';
import { formatDate } from '../../utils/formatters';
import { getTransactionDisplay } from '../../utils/transactionDisplay';
import { AttachmentThumbnails } from './AttachmentThumbnails';
import { isAdmin, isCustomer } from '../../utils/permissions';
import { AppTooltip } from '../ui/AppTooltip';

const IconAction = ({ label, tone = 'default', onClick, children }) => (
  <AppTooltip label={label}>
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      className={`flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] border shadow-sm ${
        tone === 'success'
          ? 'border-green-500/25 bg-green-500/10 text-[var(--success)] hover:bg-green-500/20'
          : tone === 'danger'
            ? 'border-red-500/25 bg-red-500/10 text-[var(--danger)] hover:bg-red-500/20'
            : 'border-blue-500/25 bg-blue-500/10 text-[var(--info)] hover:bg-blue-500/20'
      }`}
    >
      {children}
    </button>
  </AppTooltip>
);

export const TransactionsTable = ({
  transactions,
  mode,
  role,
  pageSize,
  currentPage,
  incomes,
  expenses,
  t,
  language,
  onAttachment,
  onAllowPending,
  onApproveCreate,
  onApproveUpdate,
  onDelete,
  onEdit,
}) => (
  <table className='transactions-table w-full min-w-[1280px] table-fixed border-separate border-spacing-0 text-left text-sm'>
    <colgroup>
      <col className='w-[3%]' />
      <col className='w-[8%]' />
      <col className='w-[14%]' />
      <col className='w-[9%]' />
      <col className='w-[7%]' />
      <col className='w-[8%]' />
      <col className='w-[10%]' />
      <col className='w-[8%]' />
      <col className='w-[8%]' />
      <col className='w-[16%]' />
      <col className='w-[9%]' />
    </colgroup>
    <thead>
      <tr className='text-xs uppercase text-[var(--text-muted)]'>
        {['#', 'Date', 'Customer', 'Supplier', 'Transaction', 'Document', 'Note', 'Amount', 'Payment', 'Attachments', 'Action'].map((header) => (
          <th key={header} className={`sticky top-0 z-[1] bg-[var(--surface-panel-muted)] px-3 py-3 font-bold shadow-[inset_0_-1px_0_var(--border-subtle)] ${header === 'Action' ? 'text-center' : 'text-left'}`}>
            {t(header)}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {transactions.map((item, index) => {
        const display = getTransactionDisplay(item, mode);
        const isInvoice = display.type === 'invoice';
        const pendingClientApproval = mode === 'all'
          && !item.isHistorical
          && (item.approve_status === 2 || item.approve_status === 3);
        const pendingApprovalLabel = isCustomer(role)
          ? t('Pending your approval')
          : isAdmin(role) ? t('Pending approval') : '';
        return (
          <tr
            key={item._id || `transaction-${index}`}
            className={`transition-colors ${pendingClientApproval ? 'bg-amber-50/70 hover:bg-amber-100/70 dark:bg-blue-400/[0.045] dark:hover:bg-blue-400/[0.075]' : 'hover:bg-[var(--surface-panel-muted)]'}`}
          >
            <td className={`border-b border-[var(--border-subtle)] px-4 py-4 ${pendingClientApproval ? 'border-l-2 border-l-amber-400/80 dark:border-l-blue-400/60' : ''}`}>{index + 1 + (pageSize === 'all' ? 0 : pageSize * (currentPage - 1))}</td>
            <td className='whitespace-nowrap border-b border-[var(--border-subtle)] px-3 py-4'>{formatDate(display.date, language)}</td>
            <td className='border-b border-[var(--border-subtle)] px-4 py-4 font-semibold'>
              <span className={pendingClientApproval ? 'block pl-2' : 'block'}>{display.customer || '-'}</span>
              {item.isHistorical && <div className='mt-1'><StatusBadge tone='neutral'>{t('Historical before reconciliation')}</StatusBadge></div>}
              {pendingClientApproval && pendingApprovalLabel && <div className='mt-1'><StatusBadge tone='warning' compact className='max-w-full whitespace-normal text-left'>{pendingApprovalLabel}</StatusBadge></div>}
            </td>
            <td className='border-b border-[var(--border-subtle)] px-3 py-4 [overflow-wrap:anywhere]'>{display.supplier || '-'}</td>
            <td className='border-b border-[var(--border-subtle)] px-4 py-4'>
              <StatusBadge tone={isInvoice ? 'success' : 'danger'}>
                {display.type === 'invoice' ? t('invoice') : display.type === 'payment' ? t('payment') : t('return')}
              </StatusBadge>
            </td>
            <td className='border-b border-[var(--border-subtle)] px-3 py-4 align-top'><AppTooltip label={display.document}><span className='block whitespace-normal [overflow-wrap:anywhere]'>{display.document || '-'}</span></AppTooltip></td>
            <td className='border-b border-[var(--border-subtle)] px-3 py-4 align-top'><AppTooltip label={display.note}><span className='block whitespace-normal [overflow-wrap:anywhere]'>{display.note || '-'}</span></AppTooltip></td>
            <td className='whitespace-nowrap border-b border-[var(--border-subtle)] px-4 py-4 text-right tabular-nums'>{isInvoice ? <CurrencyValue value={display.amount} /> : <CurrencyValue value={0} />}</td>
            <td className='whitespace-nowrap border-b border-[var(--border-subtle)] px-4 py-4 text-right tabular-nums'>{!isInvoice ? <CurrencyValue value={-Number(display.amount || 0)} signed /> : <CurrencyValue value={0} />}</td>
            <td className='border-b border-[var(--border-subtle)] px-3 py-4'>
              <AttachmentThumbnails attachments={display.attachments} onOpen={onAttachment} t={t} />
            </td>
            <td className='border-b border-[var(--border-subtle)] px-4 py-4'>
              <div className='flex flex-wrap items-center justify-center gap-2'>
                {mode === 'pending' ? (
                  isAdmin(role) ? <>
                    <IconAction label={t('Allow')} tone='success' onClick={() => onAllowPending(item._id, 'allow')}><FiCheck className='mx-auto' /></IconAction>
                    <IconAction label={t('Disallow')} tone='danger' onClick={() => onAllowPending(item._id, 'disallow')}><FiX className='mx-auto' /></IconAction>
                  </> : isCustomer(role) ? <IconAction label={t('Delete')} tone='danger' onClick={() => onDelete(item, true)}><FiTrash2 className='mx-auto' /></IconAction> : null
                ) : mode === 'createApproval' ? (
                  isCustomer(role)
                    ? <IconAction label={t('Allow')} tone='success' onClick={() => onApproveCreate(item._id)}><FiCheck className='mx-auto' /></IconAction>
                    : isAdmin(role) ? <IconAction label={t('Delete')} tone='danger' onClick={() => onDelete(item)}><FiTrash2 className='mx-auto' /></IconAction> : null
                ) : mode === 'updateApproval' ? (
                  isCustomer(role)
                    ? <IconAction label={t('Allow')} tone='success' onClick={() => onApproveUpdate(item)}><FiCheck className='mx-auto' /></IconAction>
                    : isAdmin(role) ? <IconAction label={t('Delete')} tone='danger' onClick={() => onDelete(item)}><FiTrash2 className='mx-auto' /></IconAction> : null
                ) : item.isHistorical ? <StatusBadge tone='neutral'>{t('Read only')}</StatusBadge> : isAdmin(role) && item.pending_transaction_id ? <>
                  <IconAction label={t('Allow')} tone='success' onClick={() => onAllowPending(item.pending_transaction?._id, 'allow')}><FiCheck className='mx-auto' /></IconAction>
                  <IconAction label={t('Disallow')} tone='danger' onClick={() => onAllowPending(item.pending_transaction?._id, 'disallow')}><FiX className='mx-auto' /></IconAction>
                </> : isAdmin(role) ? <>
                  <IconAction label={t('Edit')} onClick={() => onEdit(item)}><FiEdit2 className='mx-auto' /></IconAction>
                  <IconAction label={t('Delete')} tone='danger' onClick={() => onDelete(item, false)}><FiTrash2 className='mx-auto' /></IconAction>
                </> : isCustomer(role) ? <>
                  <IconAction label={t('Edit')} onClick={() => onEdit(item)}><FiEdit2 className='mx-auto' /></IconAction>
                  {item.pending_transaction_id && <IconAction label={t('Delete')} tone='danger' onClick={() => onDelete(item, true)}><FiTrash2 className='mx-auto' /></IconAction>}
                </> : null}
              </div>
            </td>
          </tr>
        );
      })}
      {mode === 'all' && (
        <tr className='sticky bottom-0 bg-[var(--surface-panel-muted)] font-semibold shadow-[inset_0_1px_0_var(--border-subtle)]'>
          <td className='px-4 py-4' colSpan={7}>{t('Total')}</td>
          <td className='px-4 py-4 text-right'><CurrencyValue value={incomes} /></td>
          <td className='px-4 py-4 text-right'><CurrencyValue value={-Number(expenses || 0)} signed /></td>
          <td className='px-4 py-4 text-right' colSpan={2}>{t('Balance')}: <CurrencyValue value={incomes - expenses} signed /></td>
        </tr>
      )}
    </tbody>
  </table>
);
